/**
 * ConversationsWorkbench.tsx — master/detail frame for `#conversations[/<id>]`
 * (redesign slice 5). The compact list is always mounted; only the thread pane
 * is keyed by `threadId`, so selecting a row keeps the list's filters and
 * scroll. Below `md` the route picks the pane (CSS `hidden`, no JS media
 * queries): list on `#conversations`, thread on `#conversations/<id>`.
 */

import { useTranslation } from 'react-i18next';
import type { ApiClient } from '../../api/client';
import { EmptyState } from '../../components/EmptyState';
import { ConversationDetailPage } from './ConversationDetailPage';
import { ConversationsPage } from './ConversationsPage';

export interface ConversationsWorkbenchProps {
  apiClient: ApiClient;
  threadId: string | null;
}

export const ConversationsWorkbench = ({ apiClient, threadId }: ConversationsWorkbenchProps) => {
  const { t } = useTranslation();
  return (
    <div
      data-testid="conversations-workbench"
      className="md:grid md:h-[calc(100dvh-6.85rem)] md:grid-cols-[minmax(320px,380px)_minmax(0,1fr)]"
    >
      <div className={`min-h-0 ${threadId != null ? 'hidden md:block' : ''}`}>
        <ConversationsPage apiClient={apiClient} selectedThreadId={threadId} />
      </div>
      <div
        className={`min-h-0 md:overflow-y-auto md:pl-4 ${threadId == null ? 'hidden md:block' : ''}`}
      >
        {threadId != null ? (
          <ConversationDetailPage key={threadId} apiClient={apiClient} threadId={threadId} />
        ) : (
          <EmptyState
            data-testid="conversations-select-prompt"
            title={t('conversations.selectPrompt')}
          />
        )}
      </div>
    </div>
  );
};
